import { useState } from 'react';
import { Icon } from '../../utils/icons';

const AppointmentHours = ({ config, onConfigChange }) => {
    const [showDateSpecificModal, setShowDateSpecificModal] = useState(false);
    const [dateSpecificForm, setDateSpecificForm] = useState({
        startDate: '',
        endDate: '',
        hours: { start: '10:00', end: '19:00' },
        available: true
    });

    // Default hours configuration - 10 AM to 7 PM, all days enabled
    const defaultHours = {
        monday: { available: true, start: '10:00', end: '19:00' },
        tuesday: { available: true, start: '10:00', end: '19:00' },
        wednesday: { available: true, start: '10:00', end: '19:00' },
        thursday: { available: true, start: '10:00', end: '19:00' },
        friday: { available: true, start: '10:00', end: '19:00' },
        saturday: { available: true, start: '10:00', end: '19:00' },
        sunday: { available: true, start: '10:00', end: '19:00' }
    };

    const hours = config.availableHours || defaultHours;
    const dateSpecificHours = config.dateSpecificHours || [];
    const minAdvanceBooking = config.minAdvanceBooking || { value: 1, unit: 'hour' };
    const maxAdvanceBooking = config.maxAdvanceBooking || { value: 2, unit: 'months' };
    const timezone = config.timezone || '(GMT+05:30) Asia/Colombo';

    // Time options for dropdowns (24-hour format)
    const timeOptions = [];
    for (let hour = 0; hour < 24; hour++) {
        for (let minute of [0, 15, 30, 45]) {
            const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
            const displayTime = new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
            timeOptions.push({ value: timeString, label: displayTime });
        }
    }

    // Advance booking options
    const advanceBookingOptions = [
        // Minutes
        { value: 15, unit: 'minutes', label: '15 minutes' },
        { value: 30, unit: 'minutes', label: '30 minutes' },
        { value: 45, unit: 'minutes', label: '45 minutes' },
        // Hours
        { value: 1, unit: 'hour', label: '1 hour' },
        { value: 2, unit: 'hours', label: '2 hours' },
        { value: 4, unit: 'hours', label: '4 hours' },
        { value: 8, unit: 'hours', label: '8 hours' },
        { value: 12, unit: 'hours', label: '12 hours' },
        { value: 24, unit: 'hours', label: '24 hours' },
        // Days
        { value: 2, unit: 'days', label: '2 days' },
        { value: 3, unit: 'days', label: '3 days' },
        { value: 7, unit: 'days', label: '1 week' },
        // Weeks/Months
        { value: 2, unit: 'weeks', label: '2 weeks' },
        { value: 1, unit: 'month', label: '1 month' },
        { value: 2, unit: 'months', label: '2 months' },
        { value: 3, unit: 'months', label: '3 months' },
        { value: 6, unit: 'months', label: '6 months' }
    ];

    // Timezone options
    const timezoneOptions = [
        '(GMT-12:00) International Date Line West',
        '(GMT-11:00) Coordinated Universal Time-11',
        '(GMT-10:00) Hawaii',
        '(GMT-09:00) Alaska',
        '(GMT-08:00) Pacific Time (US & Canada)',
        '(GMT-07:00) Mountain Time (US & Canada)',
        '(GMT-06:00) Central Time (US & Canada)',
        '(GMT-05:00) Eastern Time (US & Canada)',
        '(GMT-04:00) Atlantic Time (Canada)',
        '(GMT-03:00) Buenos Aires, Georgetown',
        '(GMT-02:00) Mid-Atlantic',
        '(GMT-01:00) Azores',
        '(GMT+00:00) Greenwich Mean Time',
        '(GMT+01:00) Brussels, Copenhagen, Madrid, Paris',
        '(GMT+02:00) Cairo, Athens',
        '(GMT+03:00) Moscow, St. Petersburg',
        '(GMT+04:00) Abu Dhabi, Muscat',
        '(GMT+05:00) Islamabad, Karachi',
        '(GMT+05:30) Chennai, Kolkata, Mumbai, New Delhi',
        '(GMT+05:30) Asia/Colombo',
        '(GMT+06:00) Almaty, Novosibirsk',
        '(GMT+07:00) Bangkok, Hanoi, Jakarta',
        '(GMT+08:00) Beijing, Perth, Singapore, Hong Kong',
        '(GMT+09:00) Osaka, Sapporo, Tokyo',
        '(GMT+10:00) Canberra, Melbourne, Sydney',
        '(GMT+11:00) Magadan, Solomon Is., New Caledonia',
        '(GMT+12:00) Auckland, Wellington'
    ];

    const daysOfWeek = [
        { key: 'monday', name: 'Monday' },
        { key: 'tuesday', name: 'Tuesday' },
        { key: 'wednesday', name: 'Wednesday' },
        { key: 'thursday', name: 'Thursday' },
        { key: 'friday', name: 'Friday' },
        { key: 'saturday', name: 'Saturday' },
        { key: 'sunday', name: 'Sunday' }
    ];

    const updateHours = (updates) => {
        onConfigChange({
            availableHours: { ...hours, ...updates }
        });
    };

    const updateDayHours = (day, field, value) => {
        updateHours({
            [day]: { ...hours[day], [field]: value }
        });
    };


    const addDateSpecificHours = () => {
        if (!dateSpecificForm.startDate || !dateSpecificForm.endDate) {
            alert('Please select both start and end dates');
            return;
        }

        const newDateSpecific = {
            id: Date.now().toString(),
            startDate: dateSpecificForm.startDate,
            endDate: dateSpecificForm.endDate,
            hours: dateSpecificForm.hours,
            available: dateSpecificForm.available
        };

        onConfigChange({
            dateSpecificHours: [...dateSpecificHours, newDateSpecific]
        });

        // Reset form
        setDateSpecificForm({
            startDate: '',
            endDate: '',
            hours: { start: '08:00', end: '17:00' },
            available: true
        });
        setShowDateSpecificModal(false);
    };

    const removeDateSpecificHours = (id) => {
        onConfigChange({
            dateSpecificHours: dateSpecificHours.filter(item => item.id !== id)
        });
    };

    const formatDateRange = (startDate, endDate) => {
        const start = new Date(startDate).toLocaleDateString();
        const end = new Date(endDate).toLocaleDateString();
        return start === end ? start : `${start} - ${end}`;
    };

    const formatTime12Hour = (time24) => {
        return new Date(`2000-01-01T${time24}`).toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    return (
        <div className="p-4 h-full overflow-y-auto">
            <div className="max-w-2xl mx-auto">
                <h3 className="text-lg font-semibold mb-2">Hours & Availability</h3>
                <p className="text-sm text-gray-400 mb-8">
                    Configure when customers can book appointments
                </p>

                <div className="space-y-8">
                    {/* Available Hours */}
                    <div>
                        <h4 className="text-sm font-medium text-gray-300 mb-4 border-b border-gray-700 pb-2">Available Hours</h4>

                        <div className="space-y-0">
                            {daysOfWeek.map((day) => (
                                <div key={day.key} className="space-y-3 py-4 border-b border-gray-700 last:border-b-0">
                                    {/* Top Row: Day Name and Switch Toggle */}
                                    <div className="flex items-center justify-between">
                                        <div className="text-sm text-gray-300 font-medium">
                                            {day.name}
                                        </div>

                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={hours[day.key]?.available || false}
                                                onChange={(e) => updateDayHours(day.key, 'available', e.target.checked)}
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                        </label>
                                    </div>

                                    {/* Bottom Row: Time Selection */}
                                    <div className="flex items-center justify-center">
                                        {hours[day.key]?.available ? (
                                            <div className="flex items-center space-x-3">
                                                <select
                                                    value={hours[day.key]?.start || '08:00'}
                                                    onChange={(e) => updateDayHours(day.key, 'start', e.target.value)}
                                                    className="bg-gray-800 text-white text-sm px-4 py-2.5 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none min-w-[110px] transition-colors"
                                                >
                                                    {timeOptions.map((time) => (
                                                        <option key={time.value} value={time.value}>
                                                            {time.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                <span className="text-gray-400 text-sm font-medium px-2">to</span>
                                                <select
                                                    value={hours[day.key]?.end || '17:00'}
                                                    onChange={(e) => updateDayHours(day.key, 'end', e.target.value)}
                                                    className="bg-gray-800 text-white text-sm px-4 py-2.5 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none min-w-[110px] transition-colors"
                                                >
                                                    {timeOptions.map((time) => (
                                                        <option key={time.value} value={time.value}>
                                                            {time.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        ) : (
                                            <span className="text-gray-500 text-sm italic">Unavailable for booking</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Date-Specific Hours */}
                    <div>
                        <h4 className="text-sm font-medium text-gray-300 mb-4 border-b border-gray-700 pb-2">Date-Specific Hours</h4>

                        {/* Date-Specific Hours List */}
                        {dateSpecificHours.length > 0 && (
                            <div className="space-y-2 mb-4">
                                {dateSpecificHours.map((item) => (
                                    <div key={item.id} className="bg-gray-800 rounded-lg p-3 flex items-center justify-between">
                                        <div>
                                            <div className="text-sm text-white font-medium">
                                                {formatDateRange(item.startDate, item.endDate)}
                                            </div>
                                            <div className="text-xs text-gray-400">
                                                {item.available ?
                                                    `${formatTime12Hour(item.hours.start)} - ${formatTime12Hour(item.hours.end)}` :
                                                    'Unavailable'
                                                }
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => removeDateSpecificHours(item.id)}
                                            className="text-red-400 hover:text-red-300 transition-colors"
                                        >
                                            <Icon name="delete" size={16} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        <button
                            onClick={() => setShowDateSpecificModal(true)}
                            className="w-full bg-gray-800 hover:bg-gray-700 text-gray-300 py-2 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2 border border-gray-700"
                        >
                            <Icon name="add" size={16} />
                            <span className="text-sm">Add Date-Specific Hours</span>
                        </button>
                    </div>

                    {/* Advance Booking Settings */}
                    <div>
                        <h4 className="text-sm font-medium text-gray-300 mb-4 border-b border-gray-700 pb-2">Advance Booking</h4>

                        <div className="space-y-4">
                            {/* Min Advance Booking */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Min Advance Booking
                                </label>
                                <select
                                    value={`${minAdvanceBooking.value}-${minAdvanceBooking.unit}`}
                                    onChange={(e) => {
                                        const [value, unit] = e.target.value.split('-');
                                        onConfigChange({
                                            minAdvanceBooking: { value: parseInt(value), unit }
                                        });
                                    }}
                                    className="w-full bg-gray-800 text-white px-3 py-2 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none text-sm"
                                >
                                    {advanceBookingOptions.filter(option =>
                                        option.unit === 'minutes' || option.unit === 'hour' || option.unit === 'hours' || option.unit === 'days'
                                    ).map((option) => (
                                        <option key={`${option.value}-${option.unit}`} value={`${option.value}-${option.unit}`}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                                <p className="text-xs text-gray-500 mt-1">
                                    Set how far in advance appointments must be booked, e.g., at least 1 hour before.
                                </p>
                            </div>

                            {/* Max Advance Booking */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Max Advance Booking
                                </label>
                                <select
                                    value={`${maxAdvanceBooking.value}-${maxAdvanceBooking.unit}`}
                                    onChange={(e) => {
                                        const [value, unit] = e.target.value.split('-');
                                        onConfigChange({
                                            maxAdvanceBooking: { value: parseInt(value), unit }
                                        });
                                    }}
                                    className="w-full bg-gray-800 text-white px-3 py-2 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none text-sm"
                                >
                                    {advanceBookingOptions.filter(option =>
                                        option.unit === 'days' || option.unit === 'weeks' || option.unit === 'month' || option.unit === 'months'
                                    ).map((option) => (
                                        <option key={`${option.value}-${option.unit}`} value={`${option.value}-${option.unit}`}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                                <p className="text-xs text-gray-500 mt-1">
                                    Define how far into the future bookings can be made, such as up to 2 months in advance.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Timezone */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Time Zone
                        </label>
                        <select
                            value={timezone}
                            onChange={(e) => onConfigChange({ timezone: e.target.value })}
                            className="w-full bg-gray-800 text-white px-3 py-2 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none text-sm"
                        >
                            {timezoneOptions.map((tz) => (
                                <option key={tz} value={tz}>
                                    {tz}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Date-Specific Hours Modal */}
                {showDateSpecificModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 shadow-2xl">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-lg font-semibold text-gray-900">Add Date-Specific Hours</h3>
                                <button
                                    onClick={() => setShowDateSpecificModal(false)}
                                    className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors"
                                >
                                    <Icon name="close" size={20} />
                                </button>
                            </div>

                            <div className="space-y-6">
                                {/* Date Range */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                                        <input
                                            type="date"
                                            value={dateSpecificForm.startDate}
                                            onChange={(e) => setDateSpecificForm(prev => ({ ...prev, startDate: e.target.value }))}
                                            className="w-full px-3 py-2.5 text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                                            style={{ colorScheme: 'light' }}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                                        <input
                                            type="date"
                                            value={dateSpecificForm.endDate}
                                            onChange={(e) => setDateSpecificForm(prev => ({ ...prev, endDate: e.target.value }))}
                                            className="w-full px-3 py-2.5 text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                                            style={{ colorScheme: 'light' }}
                                        />
                                    </div>
                                </div>

                                {/* Available Toggle */}
                                <div className="flex items-center space-x-3 py-2">
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={dateSpecificForm.available}
                                            onChange={(e) => setDateSpecificForm(prev => ({ ...prev, available: e.target.checked }))}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                    </label>
                                    <span className="text-sm font-medium text-gray-700">Available for booking</span>
                                </div>

                                {/* Time Range (only if available) */}
                                {dateSpecificForm.available && (
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
                                            <select
                                                value={dateSpecificForm.hours.start}
                                                onChange={(e) => setDateSpecificForm(prev => ({
                                                    ...prev,
                                                    hours: { ...prev.hours, start: e.target.value }
                                                }))}
                                                className="w-full px-3 py-2.5 text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all appearance-none"
                                            >
                                                {timeOptions.map((time) => (
                                                    <option key={time.value} value={time.value} className="text-gray-900">
                                                        {time.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">End Time</label>
                                            <select
                                                value={dateSpecificForm.hours.end}
                                                onChange={(e) => setDateSpecificForm(prev => ({
                                                    ...prev,
                                                    hours: { ...prev.hours, end: e.target.value }
                                                }))}
                                                className="w-full px-3 py-2.5 text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all appearance-none"
                                            >
                                                {timeOptions.map((time) => (
                                                    <option key={time.value} value={time.value} className="text-gray-900">
                                                        {time.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Modal Footer */}
                            <div className="flex items-center justify-between mt-8">
                                <button
                                    onClick={() => setShowDateSpecificModal(false)}
                                    className="px-6 py-2.5 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-200 font-medium border border-gray-300 hover:border-gray-400 min-w-[100px]"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={addDateSpecificHours}
                                    className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all duration-200 font-medium shadow-lg hover:shadow-xl min-w-[120px]"
                                >
                                    Add Hours
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AppointmentHours;
